import { CalendarOutlined, PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Flex, Form, Input, InputNumber, Modal, Progress, Radio, Select, Space, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { SkuSelect } from "../catalog/mapping";
import { CountStatusTag, NoWms, useWmsWarehouse, type StockCount } from "./common";
import { usePoll } from "@/components/tabs/WorkTabs";

const STATUSES = ["open", "done", "cancelled"] as const;
const STATUS_CHOICES = [...STATUSES, "all"] as const;
type NewCount = { kind: "full" | "cycle"; blind: boolean; zones?: string[]; locationIds?: string[]; notCountedDays?: number | null; note?: string };

/** Gudang > Hitung stok: stock opname and cycle counts (Plan §6.9). */
export function Counts() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const status = useStatusFilter(STATUS_CHOICES, "open");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [creating, setCreating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const tz = me?.workspace?.timezone;
  const manage = can("warehouse.manage");

  const filters = { warehouseId: current?.id ?? "", ...(status.value !== "all" ? { status: status.value } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["counts", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/counts", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
    placeholderData: keepPreviousData,
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.count.title")}
        </Typography.Title>
        {manage && (
          <Space>
            <Button icon={<CalendarOutlined />} onClick={() => setScheduling(true)}>
              {t("wms.cycle.title")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t("wms.count.add")}
            </Button>
          </Space>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("list.searchNumber")} />
        <StatusFilter
          filter={status}
          options={[...STATUSES.map((v) => ({ value: v, label: t(`wms.count.status.${v}`) })), { value: "all" as const, label: t("list.allStatuses") }]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/counts/export"
          query={filters}
          filename={t("wms.count.title")}
          columns={[
            { key: "number", label: t("wms.in.number") },
            { key: "warehouseName", label: t("orders.warehouse") },
            { key: "kind", label: t("wms.count.kind"), values: { full: t("wms.count.kind.full"), cycle: t("wms.count.kind.cycle") } },
            { key: "blind", label: t("wms.count.blind"), values: { true: t("common.yes"), false: t("common.no") } },
            { key: "status", label: t("common.status"), values: Object.fromEntries(STATUSES.map((v) => [v, t(`wms.count.status.${v}`)])) },
            { key: "counted", label: t("wms.count.countedLocations"), kind: "number" },
            { key: "locations", label: t("wms.count.locations"), kind: "number" },
            ...(manage
              ? [
                  { key: "shortUnits", label: t("wms.count.shortUnits"), kind: "number" as const },
                  { key: "overUnits", label: t("wms.count.overUnits"), kind: "number" as const },
                ]
              : []),
            { key: "note", label: t("inventory.note") },
            { key: "createdName", label: t("wms.count.createdBy") },
            { key: "createdAt", label: t("wms.count.createdAt"), kind: "datetime" },
            { key: "doneAt", label: t("list.completedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<StockCount>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.count.empty") }}
        onRow={(c) => ({ onClick: () => navigate(`/app/warehouse/counts/${c.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("wms.in.number"), dataIndex: "number", width: 150, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("wms.count.kind"),
            dataIndex: "kind",
            width: 230,
            render: (v: StockCount["kind"], c) => (
              <Space size={4}>
                <Tag color={v === "full" ? "geekblue" : "cyan"}>{t(`wms.count.kind.${v}`)}</Tag>
                {c.source !== "manual" && <Tag color={c.source === "spot" ? "purple" : "blue"}>{t(`wms.count.source.${c.source}`)}</Tag>}
                {c.blind && <Tag>{t("wms.count.blind")}</Tag>}
              </Space>
            ),
          },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (s: StockCount["status"]) => <CountStatusTag status={s} /> },
          {
            title: t("wms.count.progress"),
            render: (_, c) => <Progress percent={Math.round((c.counted / Math.max(c.locations, 1)) * 100)} size="small" format={() => `${c.counted}/${c.locations}`} />,
          },
          ...(manage
            ? [
                {
                  title: t("wms.count.difference"),
                  width: 150,
                  align: "right" as const,
                  render: (_: unknown, c: StockCount) =>
                    c.shortUnits || c.overUnits ? (
                      <Space size={4}>
                        {c.shortUnits > 0 && <Tag color="warning" className="z-num">−{formatNumber(c.shortUnits)}</Tag>}
                        {c.overUnits > 0 && <Tag color="processing" className="z-num">+{formatNumber(c.overUnits)}</Tag>}
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">—</Typography.Text>
                    ),
                },
              ]
            : []),
          { title: t("wms.count.createdBy"), dataIndex: "createdName", width: 160 },
          { title: t("wms.count.createdAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      {current && <CreateCount open={creating} warehouseId={current.id} onClose={() => setCreating(false)} />}
      {current && scheduling && <CycleSchedule warehouseId={current.id} onClose={() => setScheduling(false)} />}
    </>
  );
}

type Cycle = { daysA: number; daysB: number; daysC: number; maxLocations: number };

/**
 * Jadwal hitung siklus: each day the system makes one cycle count of the
 * locations due, by the ABC class of what they hold; locations where picking
 * came up short go first.
 */
function CycleSchedule({ warehouseId, onClose }: { warehouseId: string; onClose: () => void }) {
  const { t, formatDate } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Cycle>();
  const path = { params: { path: { warehouseID: warehouseId } } };
  const current = useQuery({ queryKey: ["cycle-count", warehouseId], queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/cycle-count", path)) });
  const save = useMutation({
    mutationFn: (body: Cycle) => unwrap(api.PUT("/v1/warehouses/{warehouseID}/cycle-count", { ...path, body })),
    onSuccess: (res) => {
      queryClient.setQueryData(["cycle-count", warehouseId], res);
      void message.success(t("wms.cycle.saved"));
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const d = current.data;
  const days = (name: keyof Cycle, label: string) => (
    <Form.Item name={name} label={label} style={{ flex: "1 1 120px" }}>
      <InputNumber min={0} max={365} precision={0} suffix={t("wms.cycle.days")} style={{ width: "100%" }} />
    </Form.Item>
  );
  return (
    <Modal
      open
      title={t("wms.cycle.title")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      okButtonProps={{ disabled: !d }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.cycle.help")}</Typography.Paragraph>
      {d && (
        <Form<Cycle> form={form} layout="vertical" requiredMark={false} initialValues={d} onFinish={(v) => save.mutate(v)}>
          <Flex gap={12} wrap>
            {days("daysA", t("wms.cycle.classA"))}
            {days("daysB", t("wms.cycle.classB"))}
            {days("daysC", t("wms.cycle.classC"))}
          </Flex>
          <Form.Item name="maxLocations" label={t("wms.cycle.max")} extra={t("wms.cycle.maxHelp")} style={{ width: 220 }}>
            <InputNumber min={1} max={2000} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            title={t("wms.cycle.status", { due: d.dueNow, last: d.lastRunOn ? formatDate(d.lastRunOn) : "—" })}
          />
        </Form>
      )}
    </Modal>
  );
}

function CreateCount({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<NewCount>();
  const kind = Form.useWatch("kind", form) ?? "cycle";
  const [skus, setSkus] = useState<string[]>([]);
  const [pick, setPick] = useState<string>();

  const locs = useQuery({
    queryKey: ["locations", warehouseId, "all"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId }, query: { status: "active" } } })),
    enabled: open,
  });
  const countable = (locs.data?.items ?? []).filter((l) => l.kind !== "variance");
  const zones = [...new Set(countable.map((l) => l.zone).filter(Boolean))].sort();

  const save = useMutation({
    mutationFn: (v: NewCount) =>
      unwrap(
        api.POST("/v1/counts", {
          body: {
            warehouseId,
            kind: v.kind,
            blind: v.blind,
            zones: v.zones ?? [],
            ...(v.kind === "cycle" ? { locationIds: v.locationIds ?? [], skuIds: skus, notCountedDays: v.notCountedDays ?? 0 } : {}),
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["counts"] });
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      setSkus([]);
      onClose();
      navigate(`/app/warehouse/counts/${r.id}`);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  return (
    <Modal open={open} width={640} title={t("wms.count.add")} okText={t("wms.count.start")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ kind: "cycle", blind: true }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="kind" label={t("wms.count.kind")}>
          <Radio.Group
            options={[
              { value: "cycle", label: t("wms.count.kind.cycle") },
              { value: "full", label: t("wms.count.kind.full") },
            ]}
          />
        </Form.Item>
        <Typography.Paragraph type="secondary" style={{ marginTop: -12 }}>
          {t(kind === "full" ? "wms.count.fullHelp" : "wms.count.cycleHelp")}
        </Typography.Paragraph>
        <Form.Item name="zones" label={t("wms.count.zones")} extra={t("wms.count.zonesHelp")}>
          <Select mode="multiple" allowClear options={zones.map((z) => ({ value: z, label: z }))} placeholder={t("wms.count.allZones")} />
        </Form.Item>
        {kind === "cycle" && (
          <>
            <Form.Item name="locationIds" label={t("wms.count.locations")}>
              <Select
                mode="multiple"
                allowClear
                showSearch={{ optionFilterProp: "label" }}
                loading={locs.isPending}
                options={countable.map((l) => ({ value: l.id, label: l.code, disabled: !!l.countId }))}
                placeholder={t("wms.count.anyLocation")}
              />
            </Form.Item>
            <Form.Item label={t("wms.count.skus")} extra={t("wms.count.skusHelp")}>
              <Flex gap={8}>
                <div style={{ flex: 1 }}>
                  <SkuSelect value={pick} onChange={setPick} />
                </div>
                <Button
                  disabled={!pick}
                  onClick={() => {
                    if (pick && !skus.includes(pick)) setSkus([...skus, pick]);
                    setPick(undefined);
                  }}
                >
                  {t("common.add")}
                </Button>
              </Flex>
              {skus.length > 0 && <SkuTags ids={skus} onRemove={(id) => setSkus(skus.filter((x) => x !== id))} />}
            </Form.Item>
            <Form.Item name="notCountedDays" label={t("wms.count.notCountedDays")} extra={t("wms.count.notCountedDaysHelp")}>
              <InputNumber min={0} max={3650} precision={0} style={{ width: 160 }} />
            </Form.Item>
          </>
        )}
        <Form.Item name="blind" label={t("wms.count.blind")} valuePropName="checked" extra={t("wms.count.blindHelp")}>
          <Switch />
        </Form.Item>
        <Form.Item name="note" label={t("wms.in.note")}>
          <Input.TextArea rows={2} maxLength={300} />
        </Form.Item>
        <Typography.Text type="secondary">{t("wms.count.freezeNote")}</Typography.Text>
      </Form>
    </Modal>
  );
}

/** Chosen SKUs, shown by code. */
export function SkuTags({ ids, onRemove }: { ids: string[]; onRemove: (id: string) => void }) {
  const details = useQuery({
    queryKey: ["skus", "byIds", ids],
    queryFn: () => Promise.all(ids.map((id) => unwrap(api.GET("/v1/skus/{skuID}", { params: { path: { skuID: id } } })))),
  });
  return (
    <Space wrap style={{ marginTop: 8 }}>
      {ids.map((id) => (
        <Tag key={id} closable onClose={() => onRemove(id)} className="z-num">
          {details.data?.find((s) => s.id === id)?.code ?? "…"}
        </Tag>
      ))}
    </Space>
  );
}
